import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-object-field-explorer>` says about itself.
 *
 * Usage is a COLUMN. "This field is populated on 62% of records" is the question every person asks
 * before deciding whether a field can be retired, and putting it behind a hover makes it a thing you
 * find rather than a thing you see — which, for the number that answers the reason you opened the
 * screen, is the same as not having it.
 *
 * A field with no usage figure is NOT a field with zero usage. Measuring costs something, so a
 * surface may arrive with figures for some fields and not others. Those rows read "not measured",
 * sort to the end whichever way the column points, and are counted separately — the same
 * distinction `<caos-coverage-list>` makes for an artifact with no executable lines.
 *
 * Origin is a filter, not a search term: "show me the custom fields" is the most common thing
 * anybody does here, and a text search over names both misses custom fields that do not say so in
 * their name and matches standard ones that do.
 */
const docs: ComponentDocs = {
  aliases: ['object details', 'field list', 'schema browser', 'field usage', 'object fields'],

  answers: [
    'see every field on an object',
    'find out whether a field is actually used before retiring it',
    'show only the custom fields',
    'find all the fields of one type',
    'export an object’s schema',
  ],

  placements: ['appPage'],

  whenToUse:
    'When somebody is deciding something about an object’s SHAPE — what is on it, what is custom, ' +
    'what is safe to remove — rather than looking at one record’s data.',

  insteadUse: [
    {
      name: 'RecordFieldInspector',
      when: 'the question is about ONE RECORD’s values rather than about the object’s fields.',
    },
    {
      name: 'RelationshipList',
      when: 'the question is what points AT this object rather than what is on it.',
    },
  ],

  inputs: [
    {
      name: 'fields',
      type: 'json',
      description:
        'The object’s fields: `{ apiName, label, type, origin, populated?, recordCount? }[]`. ' +
        '`populated` absent means NOT MEASURED, which is never drawn as zero.',
      default: [],
    },
    {
      name: 'recordCount',
      attribute: 'record-count',
      type: 'number',
      description:
        'How many records the object holds, so usage can be stated without repeating the total on ' +
        'every field. A field carrying its own `recordCount` wins.',
    },
    {
      name: 'type',
      attribute: 'type',
      type: 'text',
      description:
        'Show only fields of this type. The filter’s CONTENTS are the types actually present on the ' +
        'object — offering a type nothing here uses is offering an empty result.',
      fallback: 'every type, when the attribute is absent or empty',
    },
    {
      name: 'origin',
      attribute: 'origin',
      type: 'text',
      options: ['standard', 'custom'],
      description: 'Show only standard or only custom fields.',
      fallback: 'both origins, when the attribute is absent or empty',
    },
    {
      name: 'search',
      attribute: 'search',
      type: 'text',
      description: 'Narrows by API name or label. Intersects with the two filters rather than replacing them.',
    },
    {
      name: 'sortKey',
      attribute: 'sort-key',
      type: 'text',
      options: ['apiName', 'label', 'type', 'origin', 'usage'],
      description: 'Which column orders the rows.',
      default: 'apiName',
    },
    {
      name: 'sortDirection',
      attribute: 'sort-direction',
      type: 'text',
      options: ['asc', 'desc'],
      description: 'Which way. Unmeasured usage sorts last in BOTH directions — it is not a usage of zero.',
      default: 'asc',
    },
  ],

  events: [
    { name: 'caos-field-filter', when: 'The type or origin filter changes.', carries: '`{ type, origin }`.' },
    {
      name: 'caos-field-export',
      when: 'Export is pressed.',
      carries: '`{ fields }` — the fields as currently filtered and ordered, not the whole set.',
    },
  ],

  state: [
    { name: 'visibleFields', type: 'CaosObjectField[]', description: 'The fields surviving every filter, in order.' },
    {
      name: 'neverUsed',
      type: 'CaosObjectField[]',
      description:
        'The fields MEASURED as populated on no records. Unmeasured fields are absent from this — ' +
        'they are not known to be unused, only not counted.',
    },
  ],

  parts: [
    { part: 'explorer', is: 'The outer column.' },
    { part: 'filters', is: 'The filter row.' },
    { part: 'filter', is: 'One of the two dropdowns.' },
    { part: 'search', is: 'The search box.' },
    { part: 'export', is: 'The export button.' },
    { part: 'summary', is: 'The count line, including how many are unused and how many unmeasured.' },
    { part: 'table', is: 'The composed data table.' },
    { part: 'usage', is: 'The usage cell — a bar, or the reason there is no figure.' },
  ],

  accessibility:
    'Both filters are real `<select>` elements with their own accessible names, so the two most-used ' +
    'controls on the screen are reachable and announced without sight. The summary is a ' +
    '`role="status"` live region and always states how many of the whole set survived the filters — ' +
    'a filtered list that does not say it is filtered is how somebody concludes an object has four ' +
    'fields. Each usage bar carries its own meter semantics and its percentage as a label, so the ' +
    'figure is spoken rather than conveyed only by the bar’s width.',

  examples: [
    {
      key: 'usage',
      label: 'Fields with their real usage',
      summary:
        'The column that makes this screen worth opening: two fields are populated on almost every ' +
        'record, one on none of them, and one was never measured — which the row says rather than ' +
        'reporting as zero.',
      placement: {
        component: 'caos-object-field-explorer',
        inputs: {
          'record-count': 24000,
          fields: [
            { apiName: 'Name', label: 'Account Name', type: 'Text', origin: 'standard', populated: 24000 },
            { apiName: 'Industry', label: 'Industry', type: 'Picklist', origin: 'standard', populated: 14880 },
            { apiName: 'LegacyCode__c', label: 'Legacy Code', type: 'Text', origin: 'custom', populated: 0 },
            { apiName: 'CreditRating__c', label: 'Credit Rating', type: 'Formula', origin: 'custom' },
            { apiName: 'Website', label: 'Website', type: 'Url', origin: 'standard', populated: 3120 },
          ],
        },
      },
      stage: { needsWidth: 820 },
    },
    {
      key: 'custom-only',
      label: 'Filtered to the custom fields',
      summary:
        'Origin is a filter rather than a search term. The summary says five of nine survived, so ' +
        'nobody reads the shortened list as the whole object.',
      placement: {
        component: 'caos-object-field-explorer',
        inputs: {
          origin: 'custom',
          'record-count': 24000,
          fields: [
            { apiName: 'Name', label: 'Account Name', type: 'Text', origin: 'standard', populated: 24000 },
            { apiName: 'Industry', label: 'Industry', type: 'Picklist', origin: 'standard', populated: 14880 },
            { apiName: 'LegacyCode__c', label: 'Legacy Code', type: 'Text', origin: 'custom', populated: 0 },
            { apiName: 'Region__c', label: 'Region', type: 'Picklist', origin: 'custom', populated: 21000 },
            { apiName: 'CreditRating__c', label: 'Credit Rating', type: 'Formula', origin: 'custom' },
          ],
        },
      },
      stage: { needsWidth: 820 },
    },
  ],
};

export default docs;
